import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Markup } from '../models/markup';
import { ProjectDrawingsService } from './project-drawings.service';
import { AuthService } from './auth.service';
import { MarkupService } from './markup.service';
import { CropBox } from '../helpers/sheet-preview-crop';
import { markupBBox, recordAsBuiltRows } from '../../modules/projects/pages/projects-details-page/components/tab-screens/drawings/drawing-as-built';

export interface SheetPreviewPage {
  /** The page render the viewer uses. */
  previewUrl: string;
  attachmentUuid: string;
  pageNumber: number;
  /** W71: the set PDF, for marking the page up from a record's card */
  originalUrl?: string | null;
  /** W71: the editor's title ("C-112 Rev B") */
  sheetNumber?: string | null;
  label?: string | null;
}

/** Cards in a list all look up at once; only this many requests are in flight. */
const MAX_CONCURRENT = 3;

/** Signed image urls expire; the page lookup is reused for a few minutes only. */
const CACHE_MS = 10 * 60 * 1000;

/**
 * What the linked-sheet card needs to draw its own preview: the revision's page render (cached per revision, one
 * getRevision per revision) and the record's tagged shared markup preview on that page. No PDF is fetched.
 */
@Injectable({ providedIn: 'root' })
export class SheetPreviewService {
  private drawings = inject(ProjectDrawingsService);
  private markups = inject(MarkupService);
  private auth = inject(AuthService);
  private pages = new Map<string, { at: number; page: Promise<SheetPreviewPage | null> }>();
  /** The account the cache belongs to: logout only clears NgRx, so a new login must not see the last user's urls. */
  private cacheOwner: string | number | null = null;
  private active = 0;
  private waiting: (() => void)[] = [];

  /** One lookup per revision: concurrent cards share the in-flight request, later ones the cached answer. */
  page(projectId: string, revisionUuid: string): Promise<SheetPreviewPage | null> {
    this.syncOwner();
    const key = `${projectId}:${revisionUuid}`;
    const hit = this.pages.get(key);
    if (hit && Date.now() - hit.at < CACHE_MS) return hit.page;
    const page = this.limited(() => firstValueFrom(this.drawings.getRevision(projectId, revisionUuid))).then(revision => revision?.preview_url
      ? { previewUrl: revision.preview_url, attachmentUuid: revision.file?.attachment_uuid, pageNumber: revision.page_number,
        originalUrl: revision.file?.original_url ?? null, sheetNumber: revision.sheet?.number ?? null, label: revision.label ?? null }
      : null);
    this.pages.set(key, { at: Date.now(), page });
    // A failure is not cached: the next look retries.
    page.catch(() => { if (this.pages.get(key)?.page === page) this.pages.delete(key); });
    return page;
  }

  /** The preview image of the shared markup row tagged with this link on the page, or null. */
  async linkMarkupPreview(page: SheetPreviewPage, linkUuid: string): Promise<string | null> {
    return (await this.linkMarkupLayer(page, linkUuid))?.previewUrl ?? null;
  }

  /** Audit 3: that row's preview and the box its strokes cover on the page (the card's crop fits to it), or null */
  async linkMarkupLayer(page: SheetPreviewPage, linkUuid: string): Promise<{ previewUrl: string; box: CropBox | null } | null> {
    if (!page.attachmentUuid || !linkUuid) return null;
    const rows: Markup[] = await this.limited(() => firstValueFrom(this.markups.getByAttachment(page.attachmentUuid, page.pageNumber)));
    // The record's sketch; or (7.2) its as-built mark when its markup was made as-built, the newest one
    const mine = (rows || []).filter(r => r.link_uuid === linkUuid && r.visibility !== 'private' && r.preview_url);
    const row = mine.find(r => r.kind !== 'as_built') ?? recordAsBuiltRows(mine, linkUuid)[0];
    return row ? { previewUrl: row.preview_url, box: markupBBox(row.annotation_data, row.canvas_width, row.canvas_height) } : null;
  }

  private syncOwner(): void {
    const id = this.auth.getCurrentUser()?.id ?? null;
    if (id !== this.cacheOwner) {
      this.pages.clear();
      this.cacheOwner = id;
    }
  }

  /** Runs `task` once fewer than MAX_CONCURRENT requests are in flight. */
  private async limited<T>(task: () => Promise<T>): Promise<T> {
    if (this.active >= MAX_CONCURRENT) await new Promise<void>(resolve => this.waiting.push(resolve));
    this.active++;
    try {
      return await task();
    } finally {
      this.active--;
      this.waiting.shift()?.();
    }
  }
}
